import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { Plus, Home } from "lucide-react";
import logo from "assets/logo/logo.png";
import { useAuth } from "context/AuthProvider";
import InteractiveMap from "Components/Map/InteractiveMap";
import MapPointList from "Components/Map/MapPointList";
import MapPointForm from "Components/Map/MapPointForm";
import TypeFilter from "Components/Map/TypeFilter";
import {
  POINT_TYPE_LIST,
  sortPoints,
  getCreatableTypes,
  canManagePoint,
} from "Components/Map/mapPointTypes";
import { getAllMapPoints, deleteMapPoint } from "services/mapPointService";
import { getAllLocations } from "services/locationService";

const MapPage = () => {
  const { user } = useAuth();
  const creatableTypes = getCreatableTypes(user);
  const canAdd = creatableTypes.length > 0;

  const [points, setPoints] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [focus, setFocus] = useState(null);
  const [formPoint, setFormPoint] = useState(undefined); // undefined = closed, null = create, object = edit
  const mapSectionRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [fetchedPoints, fetchedLocations] = await Promise.all([
        getAllMapPoints(),
        getAllLocations(),
      ]);
      setPoints(sortPoints(fetchedPoints));
      setLocations(fetchedLocations);
      setLoadError("");
    } catch (err) {
      Sentry.captureException(err);
      console.error("Error loading map data:", err);
      setLoadError("Impossible de charger la carte pour le moment.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const locationsById = useMemo(
    () => Object.fromEntries(locations.map((l) => [l.id, l])),
    [locations]
  );

  const counts = useMemo(() => {
    const c = { all: points.length };
    POINT_TYPE_LIST.forEach((t) => {
      c[t.key] = points.filter((p) => p.type === t.key).length;
    });
    return c;
  }, [points]);

  const filteredPoints = useMemo(
    () => (typeFilter === "all" ? points : points.filter((p) => p.type === typeFilter)),
    [points, typeFilter]
  );

  const handleFocus = (id) => {
    setSelectedId(id);
    setFocus({ id, key: Date.now() });
    mapSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleDelete = async (point) => {
    if (!window.confirm(`Supprimer « ${point.name} » de la carte ?`)) return;
    try {
      await deleteMapPoint(point.id);
      setPoints((prev) => prev.filter((p) => p.id !== point.id));
      if (selectedId === point.id) setSelectedId(null);
    } catch (err) {
      Sentry.captureException(err);
      console.error("Error deleting map point:", err);
      window.alert("Erreur lors de la suppression.");
    }
  };

  const handleSaved = async () => {
    setFormPoint(undefined);
    await load();
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col text-slate-50 w-full">
      {/* Compact sticky navbar, same style as the location page */}
      <nav className="sticky top-0 z-50 bg-slate-900 shadow-lg">
        <div className="flex items-center justify-between px-4 py-2 gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/" className="flex items-center gap-2 text-slate-50 flex-shrink-0">
              <img src={logo} alt="Logo" className="h-8" />
            </Link>
            <span className="text-lg font-semibold italic text-slate-200 truncate">
              Carte interactive
            </span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 text-sm text-white rounded font-bold hover:bg-slate-600 transition"
            >
              <Home size={16} /> Accueil
            </Link>
            {canAdd && (
              <button
                  type="button"
                  onClick={() => setFormPoint(null)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-sm text-white rounded font-bold hover:bg-green-700 transition"
                >
                  <Plus size={16} />
                  <span className="hidden sm:inline">Ajouter un lieu</span>
                </button>
            )}
          </div>
        </div>
      </nav>

      <main className="flex-1 w-full max-w-7xl mx-auto px-4 py-6 space-y-6">
        <header className="space-y-2">
          <h1 className="font-second_font text-3xl md:text-4xl">Où jouer au Québec</h1>
          <p className="text-slate-300 max-w-3xl">
            Tournois hebdomadaires, clubs, écoles et lieux avec une table de babyfoot. Cliquez sur
            un repère pour les détails, ou utilisez la liste sous la carte.
          </p>
        </header>

        {/* Legend / filter */}
        <TypeFilter value={typeFilter} onChange={setTypeFilter} counts={counts} />

        {/* Map */}
        <section ref={mapSectionRef} className="scroll-mt-16">
          {loadError ? (
            <p className="text-center text-red-400 py-10">{loadError}</p>
          ) : (
            <InteractiveMap
              points={filteredPoints}
              locationsById={locationsById}
              selectedId={selectedId}
              onSelect={setSelectedId}
              focus={focus}
              className="h-[60vh] min-h-[360px] border border-slate-800 shadow-xl"
            />
          )}
        </section>

        {/* List */}
        <section>
          {loading ? (
            <p className="text-center text-slate-400 py-10">Chargement…</p>
          ) : (
            <MapPointList
              points={filteredPoints}
              locationsById={locationsById}
              selectedId={selectedId}
              onFocus={handleFocus}
              user={user}
              onEdit={(p) => setFormPoint(p)}
              onDelete={handleDelete}
            />
          )}
          {!loading && points.length === 0 && canAdd && (
            <p className="text-center text-slate-400 mt-2">
              Utilisez « Ajouter un lieu » pour placer le premier repère.
            </p>
          )}
        </section>
      </main>

      {formPoint !== undefined && canAdd && (formPoint === null || canManagePoint(user, formPoint)) && (
        <MapPointForm
          point={formPoint}
          locations={locations}
          allowedTypes={
            formPoint && !creatableTypes.includes(formPoint.type)
              ? [formPoint.type]
              : creatableTypes
          }
          currentUserId={user?.uid}
          onClose={() => setFormPoint(undefined)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
};

export default MapPage;
